import { useDevicesList, useWebSocket } from '@vueuse/core'
import { Message } from 'element-ui'
import Recorder from '@jtfe/js-audio-recorder'
import { ComputedRef, nextTick, ref, watch } from 'vue'

export enum EnumRecorderStatus {
  Init,
  StartLoading,
  Recording,
  Paused,
}

const RECORDER_ERROR_MESSAGE: Record<string, string> = {
  NotAllowedError: "您禁止了此网址使用麦克风",
  NotFoundError: "未找到可用麦克风"
}

function getRecorder(config?: {
  onProgress?: (data: ArrayBuffer, perDuration: number) => void
}) {
  const recorder = new Recorder({
    sampleBits: 16, // 采样位数，支持 8 或 16，默认是16
    sampleRate: 16000, // 采样率，支持 11025、16000、22050、24000、44100、48000，根据浏览器默认值，我的chrome是48000
    numChannels: 1, // 声道，支持 1 或 2， 默认是1
    bufferSize: 16384,
    streamMode: true
  })
  recorder.onprogress = ({ lBufferData, rBufferData, perDuration }) => {
    const pcm: DataView = recorder.transformIntoPCM(lBufferData, rBufferData)
    config?.onProgress?.(pcm.buffer, perDuration)
  }

  return recorder
}

export function useRecorder(config?: {
  onProgress?: (data: ArrayBuffer, perDuration: number) => void
  onDeviceError?: () => void // 设备忽然断开
}) {
  let recorder: Recorder | undefined = undefined

  const status = ref<EnumRecorderStatus>(EnumRecorderStatus.Init)

  const start = () => {
    if(!recorder) {
      recorder = getRecorder(config)
    }
    switch (status.value) {
      case EnumRecorderStatus.Init:
        status.value = EnumRecorderStatus.StartLoading
        return recorder.start().then(() => {
          status.value = EnumRecorderStatus.Recording
        }).catch(err => {
          status.value = EnumRecorderStatus.Init
          console.dir(err)
          const msg = RECORDER_ERROR_MESSAGE[err?.name] ?? '录音失败'
          Message.error(msg)
          recorder = undefined
          throw err
        })
      case EnumRecorderStatus.Paused:
        recorder.resume()
        status.value = EnumRecorderStatus.Recording
        return Promise.resolve()
      default:
        break
    }
    return Promise.reject()
  }

  const pause = () => {
    if(status.value === EnumRecorderStatus.Recording) {
      recorder?.pause()
      status.value = EnumRecorderStatus.Paused
    }
  }

  const stop = () => {
    switch (status.value) {
      case EnumRecorderStatus.Paused:
      case EnumRecorderStatus.Recording:
        recorder?.stop()
        status.value = EnumRecorderStatus.Init
        nextTick(() => {
          destroy()
        })
        break

      default:
        break
    }
  }

  const destroy = () => {
    status.value = EnumRecorderStatus.Init
    recorder?.destroy().finally(() => {
      recorder = undefined
    })
  }

  // 选择设备
  const hasAudioDevice = ref<boolean>(false)
  const { audioInputs: microphones } = useDevicesList({
    requestPermissions: false,
    constraints: {
      audio: true,
      video: false,
    },
    onUpdated () {
      if (!microphones.value.length) {
        hasAudioDevice.value = false
      } else {
        hasAudioDevice.value = true
      }
    },
  })

  watch(() => hasAudioDevice.value, (v, old) => {
    if (old && !v) {
      stop()
      config?.onDeviceError?.()
    }
  })

  return {
    recorder,
    status,
    start,
    pause,
    stop,
    destroy,
  }
}

const START_PARAMS = {
  command: 'START',
  config: {
    addPunc: true,
    audioFormat: 'pcm_s16le_16k',
    digitNorm: true,
    interimResults: true,
    makeParagraph: false,
    nbest: 1,
    textSmooth: true,
    tppContextRange: 0,
    vadMaxSegment: 30,
    wordTpp: true,
    vadHead: 0,
  }
}

const PAUSE_PARAMS = {
  command: 'PAUSE'
}

const RESUME_PARAMS = {
  command: 'CONTINUE'
}

const STOP_PARAMS = {
  command: 'END',
  cancel: false
}


export enum EnumAsrWebsocketStatus {
  Init,
  StartLoading,
  Going,
  Paused,
  Ending
}

export enum AsrRtTaskStatusEnum {
  INIT, // 初始
  GOING, // 进行中
  PAUSED // 暂停识别状态
}

enum EnumAsrWebsocketRespType {
  START = "START",
  PAUSE = "PAUSE",
  CONTINUE = "CONTINUE",
  END = "END",
  FATAL_ERROR = "FATAL_ERROR",
  RESULT = "RESULT",
  EVENT = "EVENT"
}

interface IAsrResponse {
  respType: EnumAsrWebsocketRespType,
  traceToken?: string
  errMessage?: string
  sentence?: {
    absoluteTime: number
    endTime: number
    isFinal: boolean
    property: string
    result?: {
      confidence: number,
      text: string
    }
    startTime: number
  },
  event: 'OVERTIME' | undefined, // 会话是否超时
  timestamp: number // 会话持续时长
}

export interface IAsrResult {
  absoluteTime: number
  startTime: number
  endTime: number
  isFinal: boolean
  text?: string
}

export function useAsrWebsocket(
  url: ComputedRef<string>,
  onAsrResultChange?: (rs: IAsrResult) => void,
  onClosed?: () => void,
  onError?: (isConnectError?: boolean) => void, // 连接发生错误
  getTransTimeInfo?: (timeStamp: number, isOverTime: boolean) => void // 获取一个会话从开始到现在的持续时间，以及是否超过时长限制
) {
  const { status: wsStatus, send, open, close } = useWebSocket<string>(url, {
    autoReconnect: false,
    immediate: false,
    onMessage(_, event) {
      let resData: IAsrResponse | undefined = undefined
      try {
        resData = JSON.parse(event.data)
      } catch (error) {
        console.error(error)
      }
      if(resData) {
        switch (resData.respType) {
          case EnumAsrWebsocketRespType.START:
          case EnumAsrWebsocketRespType.CONTINUE:
            status.value = EnumAsrWebsocketStatus.Going
            break
          case EnumAsrWebsocketRespType.PAUSE:
            status.value = EnumAsrWebsocketStatus.Paused
            break
          case EnumAsrWebsocketRespType.END:
            nextTick(() => destroy())
            break
          case EnumAsrWebsocketRespType.FATAL_ERROR:
            Message.error('录音已停止！' + resData.errMessage ?? '')
            nextTick(() => destroy())
            break
          case EnumAsrWebsocketRespType.RESULT:
            if (resData.sentence) {
              onAsrResultChange?.({
                absoluteTime: resData.sentence.absoluteTime,
                startTime: resData.sentence.startTime,
                endTime: resData.sentence.endTime,
                isFinal: resData.sentence.isFinal,
                text: resData.sentence.result?.text
              })
              // console.log(JSON.stringify({
              //   absoluteTime: resData.sentence.absoluteTime,
              //   startTime: resData.sentence.startTime,
              //   endTime: resData.sentence.endTime,
              //   isFinal: resData.sentence.isFinal,
              //   text: resData.sentence.result?.text
              // }))
            }
            break
          case EnumAsrWebsocketRespType.EVENT:
            getTransTimeInfo?.(resData.timestamp, resData.event === 'OVERTIME')
            break
          default:
            break
        }
      }
    },
    onDisconnected(ws, e) {
      console.log('onDisconnected: ', {ws, e})
      // 连接完成还在loading过程就断开，说明发生错误了
      if(status.value === EnumAsrWebsocketStatus.StartLoading) {
        onError?.()
        !e.reason && Message.error('服务请求响应出错')
      }
      // 正在识别过程中发生断开，可能服务发生错误或者识别过程中关闭标签页，这种需要重连
      if(status.value === EnumAsrWebsocketStatus.Going) {
        onError?.(true)
      }
      status.value = EnumAsrWebsocketStatus.Init
      if(e.reason) {
        Message.error(e.reason)
      }
      onClosed?.()
    },
    onError(ws, event) {
      console.log('onError: ', {ws, event})
      onError?.(true)
      status.value = EnumAsrWebsocketStatus.Init
    },
    onConnected(ws) {
      console.log('onConnected:', ws)
    }
  })
  const status = ref<EnumAsrWebsocketStatus>(EnumAsrWebsocketStatus.Init)

  // 开始 asr Realtime
  const start = () => {
    const params = {
      ...START_PARAMS,
    }
    console.log('start status: ', status.value)
    switch (status.value) {
      case EnumAsrWebsocketStatus.Init:
        if(wsStatus.value === "CLOSED") {
          open()
        }
        status.value = EnumAsrWebsocketStatus.StartLoading
        send(JSON.stringify(params), true)
        break
      case EnumAsrWebsocketStatus.Paused: // 继续 asr
        status.value = EnumAsrWebsocketStatus.StartLoading
        send(JSON.stringify(RESUME_PARAMS))
        break
      default:
        break
    }
  }

  // 暂停 asr
  const pause = () => {
    if(status.value === EnumAsrWebsocketStatus.Going) {
      send(JSON.stringify(PAUSE_PARAMS))
    }
  }

  // 停止 asr
  const stop = () => {
    switch (status.value) {
      case EnumAsrWebsocketStatus.Going:
      case EnumAsrWebsocketStatus.Paused:
        status.value = EnumAsrWebsocketStatus.Ending
        send(JSON.stringify(STOP_PARAMS))
        break
      default:
        break
    }
  }

  const sendAudioStream = (data: ArrayBuffer) => {
    if(status.value === EnumAsrWebsocketStatus.Going) {
      send(data)
    }
  }

  const destroy = () => {
    close()
    status.value = EnumAsrWebsocketStatus.Init
  }

  return {
    status,
    sendAudioStream,
    start,
    pause,
    stop,
    destroy,
  }
}
